import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import type { DroneMissionKey } from './dronePrescriptionLayers';

/**
 * Spectral band indexes for drone orthophotos (from TiTiler /info band_descriptions).
 * RGB preview occupies b1–b3; MS bands follow.
 */
export type DroneOrthoBandMap = {
  red: number;
  green: number;
  blue: number;
  nir: number;
  /** RedEdge — required for NDRE (Cavite only today). */
  redEdge?: number;
  /** Max DN for reflectance-style normalization (uint16 → /65535). */
  maxDn: number;
};

export const DRONE_ORTHO_BANDS: Record<DroneMissionKey, DroneOrthoBandMap> = {
  // Isabela: Red, Green, Blue, Red, NIR
  isabela: { red: 4, green: 2, blue: 3, nir: 5, maxDn: 65535 },
  // Cavite: Red, Green, Blue, Green, Red, RedEdge, NIR
  cavite: { red: 5, green: 2, blue: 3, nir: 7, redEdge: 6, maxDn: 65535 },
};

export type DroneIndexKey = 'ndvi' | 'ndre' | 'exg' | 'savi';

export type DroneIndexDef = {
  key: DroneIndexKey;
  label: string;
  /** Short farmer-friendly name shown in UI. */
  plainLabel: string;
  /** One-line plain explanation. */
  plainDescription: string;
  description: string;
  /** Typical display / class range for map colormap. */
  range: [number, number];
  /** Requires RedEdge band. */
  requiresRedEdge?: boolean;
};

export const DRONE_INDEX_DEFS: DroneIndexDef[] = [
  {
    key: 'ndvi',
    label: 'NDVI',
    plainLabel: 'Plant greenness',
    plainDescription: 'Shows how green and healthy plants look across the field.',
    description: '(NIR − Red) / (NIR + Red)',
    range: [-0.2, 0.8],
  },
  {
    key: 'ndre',
    label: 'NDRE',
    plainLabel: 'Leaf richness',
    plainDescription: 'Shows how rich and green the leaves are (good for spotting weak spots).',
    description: '(NIR − RedEdge) / (NIR + RedEdge)',
    range: [-0.2, 0.6],
    requiresRedEdge: true,
  },
  {
    key: 'exg',
    label: 'ExG',
    plainLabel: 'Green cover',
    plainDescription: 'Highlights green plant cover versus bare soil or water.',
    description: '2×G − R − B (excess green, reflectance-normalized)',
    range: [-0.5, 0.8],
  },
  {
    key: 'savi',
    label: 'SAVI',
    plainLabel: 'Plant cover (soil-adjusted)',
    plainDescription: 'Like greenness, but adjusted so bare soil does not confuse the result.',
    description: 'Soil-Adjusted Vegetation Index (L = 0.5)',
    range: [-0.3, 0.9],
  },
];

const TITILER_TILE_BASE = DIGISAKA_API_ORIGIN + '/api/tiles/WebMercatorQuad/{z}/{x}/{y}@2x.png';

/** Colormap tile URL for a computed orthophoto index (red = low → green = high). */
export function buildOrthoIndexTileUrl(
  orthoCogUrl: string,
  index: DroneIndexKey,
  bands: DroneOrthoBandMap
): string | null {
  const expression = buildOrthoIndexExpression(index, bands);
  const def = DRONE_INDEX_DEFS.find((d) => d.key === index);
  if (!expression || !def) return null;
  const [min, max] = def.range;
  const params = new URLSearchParams();
  params.set('url', orthoCogUrl);
  params.set('expression', expression);
  params.set('rescale', `${min},${max}`);
  params.set('colormap_name', 'rdylgn');
  params.set('format', 'png');
  params.set('resampling', 'bilinear');
  return `${TITILER_TILE_BASE}?${params.toString()}`;
}

const PREVIEW_BASE = DIGISAKA_API_ORIGIN + '/api/preview';

/**
 * Field-level index preview image (TiTiler /preview) for reports.
 * Pass field bbox [minX,minY,maxX,maxY] to crop to the selected field.
 */
export function buildOrthoIndexPreviewUrl(
  orthoCogUrl: string,
  index: DroneIndexKey,
  bands: DroneOrthoBandMap,
  opts?: { bbox?: [number, number, number, number]; maxSize?: number }
): string | null {
  const expression = buildOrthoIndexExpression(index, bands);
  const def = DRONE_INDEX_DEFS.find((d) => d.key === index);
  if (!expression || !def) return null;
  const [min, max] = def.range;
  const params = new URLSearchParams();
  params.set('url', orthoCogUrl);
  params.set('expression', expression);
  params.set('rescale', `${min},${max}`);
  params.set('colormap_name', 'rdylgn');
  params.set('max_size', String(opts?.maxSize ?? 640));
  params.set('format', 'png');
  if (opts?.bbox) {
    params.set('bbox', opts.bbox.join(','));
  }
  return `${PREVIEW_BASE}?${params.toString()}`;
}

export type IndexRatingParts = {
  level: 'Low' | 'Fair' | 'Good' | 'Very good' | 'Not available';
  detail: string;
  full: string;
};

/** Plain-language rating parts for badges + report UI. */
export function rateIndexParts(_index: DroneIndexKey, mean: number): IndexRatingParts {
  if (Number.isNaN(mean)) {
    return { level: 'Not available', detail: 'No result for this field.', full: 'Not available' };
  }
  if (mean < 0.1) {
    return {
      level: 'Low',
      detail: 'Plants look weak or sparse in many areas.',
      full: 'Low — plants look weak or sparse in many areas',
    };
  }
  if (mean < 0.25) {
    return {
      level: 'Fair',
      detail: 'Mixed plant condition.',
      full: 'Fair — mixed plant condition',
    };
  }
  if (mean < 0.4) {
    return {
      level: 'Good',
      detail: 'Plants look generally healthy.',
      full: 'Good — plants look generally healthy',
    };
  }
  return {
    level: 'Very good',
    detail: 'Plants look strong and green.',
    full: 'Very good — plants look strong and green',
  };
}

/** Plain-language rating from an index mean. */
export function rateIndexPlain(index: DroneIndexKey, mean: number): string {
  return rateIndexParts(index, mean).full;
}

function bn(band: number): string {
  return `b${band}`;
}

/** TiTiler rio-tiler expression for an index on a mission ortho. */
export function buildOrthoIndexExpression(
  index: DroneIndexKey,
  bands: DroneOrthoBandMap
): string | null {
  const R = bn(bands.red);
  const G = bn(bands.green);
  const B = bn(bands.blue);
  const N = bn(bands.nir);
  const dn = bands.maxDn;

  switch (index) {
    case 'ndvi':
      return `(${N}-${R})/(${N}+${R})`;
    case 'ndre': {
      if (!bands.redEdge) return null;
      const RE = bn(bands.redEdge);
      return `(${N}-${RE})/(${N}+${RE})`;
    }
    case 'exg':
      // Normalize uint16 DNs to ~0–1 reflectance so ExG stays in a sensible range
      return `2*(${G}/${dn}.0)-(${R}/${dn}.0)-(${B}/${dn}.0)`;
    case 'savi':
      // L = 0.5 on reflectance scale
      return `1.5*((${N}/${dn}.0-${R}/${dn}.0)/(${N}/${dn}.0+${R}/${dn}.0+0.5))`;
    default:
      return null;
  }
}

export function availableOrthoIndexes(bands: DroneOrthoBandMap): DroneIndexDef[] {
  return DRONE_INDEX_DEFS.filter((d) => !d.requiresRedEdge || bands.redEdge != null);
}
